import { type FC, Fragment, type ReactNode, useEffect, useState } from "react";
import { ACCOUNT_NUMBER_LENGTH, ORDER_COMMENT_LENGTH } from "~/utils/constants";
import Address, { type AddressData, type UnloadingPointData } from "../Address";
import { OrderSummary } from "~/features/cart/cartV2/OrderSummary";
import classNames from "classnames";
import { type WeightData } from "~/domains/product/types/WeightData";
import { type PriceData } from "~/domains/pricing/types/PriceData";
import {
  type CreditCardData,
  type DeliveryModeData,
  type SupportedPaymentsTypesData,
} from "~/features/cart/cart/transformers/CartResponseTransformer";
import {
  selectDeliveryModeDetails,
  selectDeliveryModes,
  selectFreightPayment,
  selectFreightPaymentDetails,
  selectQuotableGroup,
  selectTotalCheckoutValue,
  selectTotalWeight,
} from "~/features/cart/cart/redux/selectors/checkoutPageSelectors";
import { updateAddress, updateUnloadingPoint } from "~/features/cart/cart/redux/actions/checkoutPageActions";
import { type CustomDispatch } from "~/app/store/types";
import { connect, useSelector } from "react-redux";
import { type RootState } from "~/app/store";
import { Trans, useTranslation } from "react-i18next";
import AddressDropdown from "../AddressDropdown/AddressDropdown";
import { Controller, type FieldErrors, useFormContext } from "react-hook-form";
import { CheckoutOptimeSubscriptionForm } from "./CheckoutOptimeSubscriptionForm";
import { FormMandatoryLabel } from "../FormMandatoryLabel";
import {
  selectCartContainsDigitalServiceTenant,
  selectCartContainsOptime,
} from "~/features/cart/cart/redux/selectors/productEntrySelectors";
import { useOptimeCountries } from "../CheckoutOptimeCountriesContext";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { URL_GENERAL_TERMS, URL_OPTIME_TERMS } from "~/utils/url/staticUrls";
import { SimpleSelect } from "~/common/components/Select/SimpleSelect";
import { type CheckoutForm } from "../CheckoutCart";
import { DeliveryOption, DeliveryOptionItem } from "../DeliveryOption";
import { CheckboxWithLabel } from "~/common/components/CheckboxWithLabel";
import { isKITProduct } from "~/domains/product/types/ProductData";
import Radio from "~/common/components/Radio";
import AddCreditCard from "../CreditCard/addCreditCard";
import ManageCreditCard from "../CreditCard/manageCreditCard";
import { userIsConsumerDistributor, userIsDistributor } from "~/domains/user/utils/roles";
import OrderTypeSelection from "../OrderTypeSelection";
import { PAYMENT_TYPE_2_TRANSLATION_KEY } from "./constants";
import { type DropdownEntry } from "~/common/components/DropdownChoice/types";
import { Box, Grid, Stack, TextField, Typography } from "~/common/mui";
import { Alert } from "~/common/mui/Alert/Alert";
import { C_ON_SURFACE_LOW_EMPHASIS, C_PRIMARY, C_SURFACE, C_WHITE } from "~/common/components/icon/Colors";
import EditIcon from "@mui/icons-material/Edit";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { PopperTooltip } from "~/common/components/Tooltip";
import AddIcon from "@mui/icons-material/Add";
import { LinkButton } from "~/common/components/Link";
import { isTargetPriceEnabled } from "~/app/config/featureFlags";

interface SelfProps {
  actionButtons: ReactNode[];
  cartProductsDeliveryDateList?: Date[];
  surcharge?: PriceData;
  tariffSurcharge?: boolean;
  bothButtonsDisabled: boolean;
  totalDiscount?: PriceData | null;
}

interface FromStateProps {
  addresses: AddressData[];
  totalWeight: WeightData;
  totalValue: PriceData;
  soldTo: {
    name: string;
    accNo: string;
    addresses: AddressData[];
  };
  address?: AddressData;
  countriesList: {
    isocode: string;
    name: string;
  }[];
  canAddAddress: boolean;
  unloadingPoints?: Dict<UnloadingPointData[]>;
  unloadingPoint?: UnloadingPointData;
  defaultCreditCard: CreditCardData | undefined;
  supportedPaymentTypes: SupportedPaymentsTypesData[];
}

interface DispatchProps {
  updateAddress: (newAddress: AddressData, temporary?: boolean) => void;
  updateUnloadingPoint: (unloadingPoint: UnloadingPointData) => void;
}

type Props = SelfProps & FromStateProps & DispatchProps;

const Cmp = (props: Props) => {
  const {
    addresses,
    totalWeight,
    totalValue,
    address,
    soldTo,
    supportedPaymentTypes,
    defaultCreditCard,
    updateAddress,
    updateUnloadingPoint,
    actionButtons,
    countriesList,
    canAddAddress,
    unloadingPoints,
    unloadingPoint,
    surcharge,
    tariffSurcharge,
    bothButtonsDisabled,
    totalDiscount,
  } = props;

  const {
    register,
    control,
    watch,
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<CheckoutForm>();

  const { t } = useTranslation();
  const [AddressesState, setAddressesState] = useState<AddressData[]>(addresses);

  const [isOpenAdd, setIsOpenAdd] = useState(false);
  const [isOpenAddress, setIsOpenAddress] = useState(false);
  const [isOpenManage, setIsOpenManage] = useState(false);
  const [defaultCC, setDefaultCC] = useState(defaultCreditCard);

  const containsOptimeProducts = useSelector(selectCartContainsOptime);
  const containsDST = useSelector(selectCartContainsDigitalServiceTenant);

  const methodPayment = watch("paymentType");
  const deliveryCode = watch("deliveryCode");
  const freightPaymentID = watch("freightPaymentCode");
  const deliveryDetails = useSelector(selectDeliveryModeDetails(deliveryCode, methodPayment));
  const freightPaymentDetails = useSelector(selectFreightPaymentDetails(deliveryCode, methodPayment, freightPaymentID));

  const webservicesDisabled = window.SCHA.config.disableAllWS;

  const hasKitProductsInQuotables = useSelector(selectHasKitProductsInQuotables);
  const hasTargetPriceValue = useSelector(lineItemTargetPrice);
  const targetPriceEnabled = isTargetPriceEnabled();

  useEffect(() => {
    setAddressesState(addresses);
  }, [addresses]);

  const shippingMethodName = deliveryCode ? t(`data.shipmentMethod.${deliveryCode}`) : "-";

  const expressDelivery = "EXPRESS";
  let extraElements = undefined;
  if (deliveryDetails && deliveryDetails?.paymentCodeDisplay) {
    extraElements = [
      {
        label: t("checkout.shipmentInfo.label", {
          context: window.SCHA.config.translationCountryCode,
          shipmentType: shippingMethodName,
        }),
        value:
          deliveryDetails.paymentCodeDisplay === expressDelivery
            ? t(`data.paymentCodeDisplay.EXPRESS`, {
                context: window.SCHA.config.translationCountryCode,
              })
            : undefined,
      },
    ];
  }

  const availableUnloadingPoints =
    unloadingPoints && address && address.parentUnit && unloadingPoints[address.parentUnit]
      ? unloadingPoints[address.parentUnit]
      : [];

  const unloadingPointsElements = availableUnloadingPoints
    ? availableUnloadingPoints.map((element) => ({
        key: element.name,
        label: element.name,
        value: element.id,
      }))
    : [];

  const { cartProductsDeliveryDateList } = props;
  const countryByCode = useOptimeCountries().countryByCode;

  const canAddHeaderComments = userHasPermission(Permission.ADD_VIEW_HEADER_LEVEL_COMMENTS);

  const deliveryModes = useSelector(selectDeliveryModes(methodPayment));
  const FreightPaymentList = useSelector(selectFreightPayment(deliveryCode, methodPayment));

  const deliveryModeDropdownValues = (data?: DeliveryModeData[]): DropdownEntry<string>[] => {
    if (data) {
      return data.map((element) => ({
        key: element.code,
        label: t(`data.shipmentMethod.${element.code}`, {
          context: window.SCHA.config.translationCountryCode,
        }),
        value: element.code,
      }));
    } else {
      return [];
    }
  };

  // sort delivery dates, the farthers should be first
  cartProductsDeliveryDateList?.sort((a, b) => b.getTime() - a.getTime());

  const { ref: poNumberRef, ...poNumberProps } = register("poNumber", {
    required: t("checkoutPage.form.error.mandatory"),
    maxLength: {
      value: 35,
      message: t("checkoutPage.form.error.poNumber"),
    },
    validate: (value: string) => value.trim().length > 0 || t("checkoutPage.form.error.mandatory"), // Handles only-whitespace
  });

  const { ref: accountNumberRef, ...accountNumberProps } = register("freightPaymentAccountNumber", {
    required: false,
    maxLength: ACCOUNT_NUMBER_LENGTH,
    // TODO: analyse if we can do sth bette
    validate: ($: string) => !$ || $.trim().length > 0,
  });

  const isCardExpired = defaultCC && defaultCC.expired;

  const showAccountNumberInput = (errors: FieldErrors<CheckoutForm>) => {
    const selectedFreight = FreightPaymentList?.freightPayments
      ? FreightPaymentList?.freightPayments.find((item) => item.id === freightPaymentID)
      : undefined;
    if (selectedFreight && selectedFreight?.accountNumber === true) {
      return (
        <>
          <TextField
            fullWidth
            label={t("checkoutPage.headers.acccountNumber.input")}
            data-test-id={"checkout-input-accountNumber"}
            inputRef={accountNumberRef}
            error={!!errors.freightPaymentAccountNumber}
            {...accountNumberProps}
          />
          <FormMandatoryLabel />
        </>
      );
    }
    return;
  };

  const renderPaymenttText = () => {
    const renderText =
      methodPayment === "account" ? (
        <>{t("checkoutPage.payment.account")}</>
      ) : (
        <>
          {t("checkoutPage.creditCard.creditCardLabel")}
          <span className={"ml-1 mr-1 text-uppercase"}>{defaultCC?.type}</span>
          {t("checkoutPage.creditCard.creditCardLabelEnding")}
          <span className={"ml-1"}>{defaultCC?.number}</span>
        </>
      );

    return renderText;
  };

  const renderFreightText = () => {
    const filtredPayment = FreightPaymentList?.freightPayments
      ? FreightPaymentList?.freightPayments.find((item) => item.id === freightPaymentID)
      : undefined;
    const renderText = filtredPayment !== undefined ? t(`data.freightPayment.${filtredPayment?.id}`) : "-";
    return <CheckoutSummarySubItem title={renderText} />;
  };

  const filtredFreight =
    deliveryModes &&
    deliveryModes?.deliveryModes &&
    deliveryModes?.deliveryModes.find((item) => item.code === deliveryCode);

  const isDistributor = userIsDistributor() || userIsConsumerDistributor();
  const availableOrderTypes = window.SCHA.config.availableOrderTypes;
  const selectedOrderType = window.SCHA.config.selectedOrderType;

  useEffect(() => {
    if (methodPayment !== "account" && defaultCC !== undefined) {
      setValue("creditCardID", defaultCC?.customerRefNum ? defaultCC.customerRefNum : "");
    } else {
      if (methodPayment === "card-jp" && defaultCC === undefined) {
        setValue("paymentType", "account");
      }
      setValue("creditCardID", "");
    }
  }, [methodPayment, defaultCC]);

  useEffect(() => {
    if (FreightPaymentList?.freightPayments !== undefined && FreightPaymentList?.freightPayments?.length > 0) {
      setValue(
        "freightPaymentCode",
        FreightPaymentList?.freightPayments[0]?.id ? FreightPaymentList?.freightPayments[0].id : "",
      );
    } else {
      setValue("freightPaymentCode", "");
    }
  }, [FreightPaymentList]);

  const addressModal = () => (
    <AddressDropdown
      addresses={AddressesState}
      selectedAddress={address!}
      updateAddress={updateAddress}
      customerNumber={soldTo.accNo}
      countriesList={countriesList}
      canAddAddress={canAddAddress}
      onClose={() => setIsOpenAddress(false)}
    />
  );

  const isCheckedOrderInformation = () => {
    return !!watch("poNumber");
  };

  const isCheckedOptime = () => {
    const optimeObj = watch("optime") || {};
    return Object.values(optimeObj).every((value) => {
      if (value === null || value === undefined) {
        return false;
      }
      if (typeof value === "string" && value.trim() === "") {
        return false;
      }
      return true;
    });
  };

  const newSummaryDesign = () => (
    <Box
      sx={{
        backgroundColor: C_SURFACE,
      }}
      marginBottom={3}
      className="fullbleed"
    >
      <div className="container">
        <Grid container spacing={3} paddingY={4}>
          <Grid size={8}>
            <Stack gap={3}>
              <Stack gap={2} padding={2} bgcolor={C_WHITE} borderRadius={2}>
                <CheckoutSummarySectionTitle
                  title={t("checkoutPage.headers.orderInfo")}
                  checked={isCheckedOrderInformation()}
                />
                <Grid container>
                  <Grid size={6}>
                    <TextField
                      fullWidth
                      label={t("checkoutPage.headers.purchaseNumber.input")}
                      data-test-id={"checkout-input-purchase-number"}
                      error={!!errors.poNumber}
                      inputRef={poNumberRef}
                      helperText={
                        errors.poNumber ? errors.poNumber.message : t("checkoutPage.form.helperText.poNumber")
                      }
                      {...poNumberProps}
                    />
                  </Grid>

                  {isDistributor && availableOrderTypes && selectedOrderType && (
                    <Grid size={6}>
                      <Controller
                        name={"orderType"}
                        control={control}
                        render={({ field: { value, onChange } }) => {
                          return (
                            <OrderTypeSelection
                              availableOrderTypes={availableOrderTypes}
                              value={value}
                              onChange={onChange}
                            />
                          );
                        }}
                      />
                    </Grid>
                  )}
                </Grid>
                {canAddHeaderComments && (
                  <Grid size={12}>
                    <Controller
                      name={"orderComment"}
                      control={control}
                      rules={{ required: false, maxLength: ORDER_COMMENT_LENGTH }}
                      render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
                        <TextField
                          variant={"outlined"}
                          data-test-id={"checkout-header-comment"}
                          label={t("order.information.header.comment")}
                          helperText={t("order.information.header.helper")}
                          error={!!error}
                          inputRef={ref}
                          multiline
                          fullWidth
                          rows={2}
                          {...inputProps}
                        />
                      )}
                    />
                  </Grid>
                )}
                <FormMandatoryLabel />
              </Stack>
              {containsDST && (
                <Stack gap={2} padding={2} bgcolor={C_WHITE} borderRadius={2}>
                  <CheckoutSummarySectionTitle
                    title={t(`page.checkout.optime.header.form`)}
                    checked={isCheckedOptime()}
                  />
                  <Stack gap={3}>
                    <CheckoutOptimeSubscriptionForm control={control} />
                    <FormMandatoryLabel />
                    <Alert severity={"info"} className={"mb-30"}>
                      {t("page.checkout.optime.disclaimer")}
                    </Alert>
                  </Stack>
                </Stack>
              )}
              <Stack gap={2} padding={2} bgcolor={C_WHITE} borderRadius={2}>
                <CheckoutSummarySectionTitle title={t("checkoutPage.headers.shipmentAndDelivery")} checked />

                <Grid container>
                  <Grid size={6}>
                    <Stack gap={3}>
                      <Stack
                        direction={"row"}
                        sx={{
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                        }}
                      >
                        <CheckoutSummarySubItem title={t("checkoutPage.headers.shippingAddress")}>
                          {address && <Address data={address} />}
                        </CheckoutSummarySubItem>

                        <LinkButton
                          iconBefore={<EditIcon />}
                          onClick={() => {
                            setIsOpenAddress(true);
                          }}
                        >
                          {t("page.orderQuoteSettings.table.button.edit")}
                        </LinkButton>
                      </Stack>
                      {unloadingPoint && unloadingPointsElements && unloadingPointsElements.length > 0 && (
                        <SimpleSelect
                          fullWidth
                          label={t("checkoutPage.headers.unloading")}
                          values={unloadingPointsElements}
                          data-test-id={"checkout-dropdown-uploading"}
                          value={unloadingPoint!.id}
                          onChange={(e) =>
                            updateUnloadingPoint(availableUnloadingPoints.find((item) => item.id == e.target.value)!)
                          }
                        />
                      )}
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack gap={3}>
                      {deliveryModes?.deliveryModes.length == 1 ? (
                        <CheckoutSummarySubItem title={t("checkoutPage.headers.carrier")}>
                          {deliveryModes?.deliveryModes[0].name}
                        </CheckoutSummarySubItem>
                      ) : (
                        <Controller
                          name={"deliveryCode"}
                          control={control}
                          render={({ field: { value, onChange, onBlur } }) => {
                            return (
                              <SimpleSelect
                                data-test-id={"checkout-dropdown-carrier"}
                                label={t("checkoutPage.headers.carrier")}
                                onChange={(e) => {
                                  const deliveryCodeTarget = e.target.value as string;
                                  // eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
                                  const newDetails = deliveryModes?.deliveryModes.find(
                                    (item) => item.code === deliveryCodeTarget,
                                  )!;
                                  const orderType = newDetails.validOrderTypes.find(
                                    (type) => type.code === window.SCHA.config.selectedOrderType,
                                  );
                                  if (!orderType) {
                                    const newOrderType = newDetails.validOrderTypes[0].code;
                                    setValue("orderType", newOrderType);
                                    onChange(deliveryCodeTarget);
                                  } else {
                                    onChange(deliveryCodeTarget);
                                  }
                                }}
                                onBlur={onBlur}
                                value={value}
                                values={
                                  deliveryModes?.deliveryModes
                                    ? deliveryModeDropdownValues(deliveryModes?.deliveryModes)
                                    : []
                                }
                                fullWidth={true}
                              />
                            );
                          }}
                          rules={{
                            required: true,
                          }}
                        />
                      )}
                      {userHasPermission(Permission.COMPLETE_DELIVERY) && (
                        <>
                          <CheckoutSummarySubItem title={t("checkoutPage.headers.deliveryOptions.title")}>
                            <Controller
                              name={"completeDelivery"}
                              control={control}
                              defaultValue={DeliveryOptionItem.PARTIAL}
                              rules={{ required: true }}
                              render={({ field: { value, onChange } }) => (
                                <DeliveryOption
                                  onChange={onChange}
                                  value={value}
                                  estimatedDate={cartProductsDeliveryDateList && cartProductsDeliveryDateList[0]}
                                />
                              )}
                            />
                          </CheckoutSummarySubItem>
                          {watch("completeDelivery") === DeliveryOptionItem.COMPLETE && (
                            <Alert severity="info" className="mb-3">
                              {t("checkoutPage.headers.deliveryOptions.notice")}
                            </Alert>
                          )}
                        </>
                      )}
                    </Stack>
                  </Grid>

                  {deliveryDetails && deliveryDetails?.shippingConditionCodes?.length > 0 && (
                    <Grid size={12} marginTop={3}>
                      <Stack gap={1}>
                        {deliveryDetails.shippingConditionCodes.map((code) => (
                          <Alert severity="info">
                            {t(`data.shippingCondition.${code}`, {
                              context: window.SCHA.config.translationCountryCode,
                            })}
                          </Alert>
                        ))}
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Stack>
              <Stack gap={2} padding={2} bgcolor={C_WHITE} borderRadius={2}>
                <CheckoutSummarySectionTitle title={t("checkoutPage.headers.paymentAndBilling")} checked />
                <Grid container>
                  <Grid size={6}>
                    <Stack gap={3}>
                      <CheckoutSummarySubItem title={t("checkoutPage.headers.soldToAddress")}>
                        {soldTo.addresses &&
                          soldTo.addresses
                            .filter((address) => !address.editable)
                            .map((address) => <Address data={address} />)}
                      </CheckoutSummarySubItem>
                      <CheckoutSummarySubItem title={t("checkoutPage.headers.accountNumber")}>
                        {soldTo.accNo}
                      </CheckoutSummarySubItem>
                    </Stack>
                  </Grid>
                  <Grid size={6}>
                    <Stack gap={3}>
                      <CheckoutSummarySubItem title={t("checkoutPage.headers.paymentType")}>
                        {supportedPaymentTypes.length == 1 ? (
                          t(
                            PAYMENT_TYPE_2_TRANSLATION_KEY[
                              supportedPaymentTypes[0].code as keyof typeof PAYMENT_TYPE_2_TRANSLATION_KEY
                            ],
                          )
                        ) : (
                          <Controller
                            name={"paymentType"}
                            control={control}
                            render={({ field: { value, onChange } }) => (
                              <>
                                {supportedPaymentTypes?.map((type, index) => (
                                  <Fragment key={index}>
                                    {type.code === "account" && (
                                      <label className={"d-flex mb-2"}>
                                        <Radio
                                          active={value === type.code}
                                          value={type.code}
                                          onClick={onChange}
                                          className={"icon-margin"}
                                          data-test-id={""}
                                        />
                                        {t(PAYMENT_TYPE_2_TRANSLATION_KEY[type.code])}
                                      </label>
                                    )}
                                    {type.code === "card-jp" && (
                                      <>
                                        {defaultCC && (
                                          <>
                                            <label
                                              className={classNames(
                                                "d-flex",
                                                isCardExpired && "scha__order-summary-option-disabled",
                                              )}
                                            >
                                              <Radio
                                                active={value === type.code}
                                                value={type.code}
                                                onClick={onChange}
                                                className={"icon-margin"}
                                                data-test-id={""}
                                                disabled={isCardExpired}
                                              />
                                              {t(PAYMENT_TYPE_2_TRANSLATION_KEY[type.code])}
                                              <span className={"ml-1 mr-1 text-uppercase"}> {defaultCC.type}</span>
                                              {t("checkoutPage.creditCard.creditCardLabelEnding")}
                                              <span className={"ml-1 mr-1"}> {defaultCC.number.slice(-4)}</span>
                                              <a
                                                className={"scha__order-summary-credit-card"}
                                                onClick={() => setIsOpenManage(!isOpenManage)}
                                              >
                                                {t("checkoutPage.creditCard.manageCardLabel")}
                                              </a>
                                            </label>
                                            {isCardExpired && (
                                              <p className={"scha__order-summary-credit-card-expired"}>
                                                {t("checkoutPage.creditCard.expiredCardLabel")}
                                              </p>
                                            )}
                                          </>
                                        )}
                                        {(!defaultCC || defaultCC === undefined) && (
                                          <LinkButton iconBefore={<AddIcon />} onClick={() => setIsOpenAdd(!isOpenAdd)}>
                                            {t("checkoutPage.creditCard.addCardLabel")}
                                          </LinkButton>
                                        )}
                                      </>
                                    )}
                                  </Fragment>
                                ))}
                              </>
                            )}
                          />
                        )}
                      </CheckoutSummarySubItem>
                      <CheckoutSummarySubItem title={t("checkoutPage.headers.freight")}>
                        {t(getFreightHeaderLabel(deliveryDetails), {
                          context: window.SCHA.config.translationCountryCode,
                        })}
                      </CheckoutSummarySubItem>
                      {filtredFreight && filtredFreight.freightPayments && (
                        <CheckoutSummarySubItem title={t("checkoutPage.payment.freightPayment")}>
                          <Stack gap={1}>
                            <Controller
                              name={"freightPaymentCode"}
                              control={control}
                              render={({ field: { value, onChange } }) => {
                                return (
                                  <div>
                                    {filtredFreight.freightPayments.map((item, index) => (
                                      <label className={"d-flex mb-2"} key={String(index)}>
                                        <Radio
                                          active={value === item.id}
                                          value={item.id}
                                          onClick={onChange}
                                          className={"icon-margin"}
                                          data-test-id={""}
                                        />
                                        {t(`data.freightPayment.${item.id}`)}
                                      </label>
                                    ))}
                                  </div>
                                );
                              }}
                            />
                            {showAccountNumberInput(errors)}
                          </Stack>
                        </CheckoutSummarySubItem>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={4}>
            <Stack gap={4} padding={2} bgcolor={C_WHITE} borderRadius={2}>
              <CheckoutSummarySectionTitle title={t("checkoutPage.headers.orderSummary")} />
              <OrderSummary sx={{ width: "100%" }}>
                <OrderSummary.DataContainer>
                  <OrderSummary.WeightRow weight={totalWeight} />
                  <OrderSummary.SurchargeRow surcharge={surcharge} />
                  <OrderSummary.DiscountRow discount={totalDiscount} />
                  <OrderSummary.TotalRow
                    value={webservicesDisabled ? null : totalValue}
                    showPriceOnRequest
                    asterisk={!!surcharge || tariffSurcharge}
                  />
                  <OrderSummary.TaxDisclaimer />
                </OrderSummary.DataContainer>
                <OrderSummary.Divider />
                <Stack gap={4}>
                  {containsOptimeProducts && (
                    <Stack gap={1}>
                      <Typography
                        variant="labelLarge"
                        className={classNames(errors.optime?.consent ? "f-error-red" : "f-label-color")}
                      >
                        {t("page.checkout.optime.terms.header")}
                      </Typography>

                      <Controller
                        name={"optime.consent"}
                        control={control}
                        rules={{ required: true }}
                        render={({ field: { ref, onChange, value }, fieldState: { error } }) => (
                          <CheckboxWithLabel
                            data-test-id={"checkout-optime-consent-cbox"}
                            inputRef={ref}
                            onCheck={onChange}
                            checked={value}
                            error={!!error}
                          >
                            <Trans i18nKey={"page.checkout.optime.terms.label"}>
                              Yes, I confirm that I have read, understood and accept the{" "}
                              <a href={URL_OPTIME_TERMS} target={"_blank"}>
                                General Terms and Conditions for Schaeffler Digital Services
                              </a>
                              .
                            </Trans>
                          </CheckboxWithLabel>
                        )}
                      />
                    </Stack>
                  )}
                  <OrderSummary.Actions>{actionButtons}</OrderSummary.Actions>
                </Stack>
              </OrderSummary>

              {targetPriceEnabled && hasTargetPriceValue && (
                <Alert severity={"info"}>
                  <div data-test-id={"target-price-disclaimer"}>{t("page.checkout.target.price.disclaimer")}</div>
                </Alert>
              )}

              <Alert severity={"info"}>
                <Trans i18nKey={"page.checkout.legalDisclaimer"}>
                  Our deliveries and services are subject to and governed by the{" "}
                  <a href={URL_GENERAL_TERMS} target="_blank">
                    Terms and Conditions (Sales)
                  </a>{" "}
                  of Schaeffler Group to the extent the respective deliveries and services are not governed by other
                  written agreements in place between the parties. A binding contract is only concluded upon your
                  receipt of our written order confirmation which confirms the terms upon which Schaeffler has accepted
                  your order and concluded the contract of sale.
                </Trans>
              </Alert>

              {hasKitProductsInQuotables && (
                <Alert severity="info" className="mt-2">
                  <div data-test-id={"info-housing-disclaimer-1"}>
                    {t("page.checkout.housing.disclaimer.inQuotable")}
                  </div>
                </Alert>
              )}

              {bothButtonsDisabled && (
                <Alert severity="info" className="mt-2">
                  {t("feature.salesCampaign.checkout.allButtonsDisabledMsg")}
                </Alert>
              )}
            </Stack>
          </Grid>
        </Grid>
      </div>
    </Box>
  );

  return (
    <>
      {newSummaryDesign()}
      {isOpenAddress && addressModal()}
      {isOpenAdd && (
        <AddCreditCard
          updateCard={(data) => setDefaultCC(data)}
          closeModal={() => setIsOpenAdd(!isOpenAdd)}
          defaultCC={defaultCC ? true : false}
        />
      )}
      {isOpenManage && (
        <ManageCreditCard
          addNewCard={() => {
            setIsOpenManage(!isOpenManage);
            setIsOpenAdd(!isOpenAdd);
          }}
          updateCard={(data) => setDefaultCC(data)}
          closeModal={() => setIsOpenManage(!isOpenManage)}
          defaultCC={defaultCC}
        />
      )}
    </>
  );
};

export const CheckoutSummaryItem: FC<{ className?: string; title: ReactNode; children?: ReactNode }> = ({
  children,
  title,
  className,
}) => (
  <div className={classNames("my-30", className)}>
    <p className={"title-medium mt-0 mb-2"}>{title}</p>
    {children}
  </div>
);

export const CheckoutSummarySectionTitle: FC<{ className?: string; title: ReactNode; checked?: boolean }> = ({
  title,
  className,
  checked = false,
}) => (
  <Stack direction={"row"} gap={1} alignItems={"center"}>
    <Typography variant="titleLarge" component={"b"}>
      {title}
    </Typography>
    {checked && (
      <PopperTooltip tooltipContent={"Completed"} placement="top">
        <CheckCircleIcon
          sx={{
            color: C_PRIMARY,
          }}
        />
      </PopperTooltip>
    )}
  </Stack>
);

export const CheckoutSummarySubItem: FC<{ className?: string; title: ReactNode; children?: ReactNode }> = ({
  children,
  title,
  className,
}) => (
  <div className={classNames(className)}>
    <Typography variant={"bodyMedium"} color={C_ON_SURFACE_LOW_EMPHASIS} marginBottom={1}>
      {title}
    </Typography>
    {children}
  </div>
);

const mapStateToProps = (state: RootState): FromStateProps => ({
  address: state.checkoutPage.selectedAddress,
  soldTo: state.checkoutPage.soldTo,
  addresses: state.checkoutPage.addresses,
  totalValue: selectTotalCheckoutValue(state),
  totalWeight: selectTotalWeight(state),
  countriesList: state.checkoutPage.countriesList,
  canAddAddress: state.checkoutPage.canAddAddress,
  unloadingPoints: state.checkoutPage.unloadingPoints,
  unloadingPoint: state.checkoutPage.unloadingPoint,
  defaultCreditCard: state.checkoutPage.defaultCreditCard,
  supportedPaymentTypes: state.checkoutPage.supportedPaymentTypes,
});

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>): DispatchProps => ({
  updateAddress: (newAddress, temporary?) => dispatch(updateAddress(newAddress, temporary)),
  updateUnloadingPoint: (unloadingPoint) => dispatch(updateUnloadingPoint(unloadingPoint)),
});

// TODO: Wait for https://github.com/DefinitelyTyped/DefinitelyTyped/issues/31363 to be fixed
// @ts-ignore
const CheckoutSummary = connect(mapStateToProps, mapDispatchToProps)(Cmp) as FC<SelfProps>;
export default CheckoutSummary;

function getFreightHeaderLabel(deliveryDetails: DeliveryModeData | undefined | null) {
  return deliveryDetails?.paymentCodeInfo
    ? `checkoutPage.shipment.freightCharges.${deliveryDetails.paymentCodeInfo}`
    : "checkoutPage.headers.freight";
}

const selectHasKitProductsInQuotables = (state: RootState) => {
  const quotableGroup = selectQuotableGroup(state);
  if (!quotableGroup) {
    return false;
  }
  return Object.values(state.cart.packageEntriesById)
    .filter((entry) => entry.groupNumbers.includes(quotableGroup.groupNumber))
    .some((lineItem) => isKITProduct(state.products.basic[lineItem.productCode]));
};

// Added Method to check if any line Item has the target price
export const lineItemTargetPrice = (state: RootState): boolean => {
  const entries = Object.values(state.cart.packageEntriesById);
  return entries.some((lineItem) => {
    const price = lineItem.targetPrice?.value;
    return !!price;
  });
};
